import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Receipt,
  ShoppingCart,
  Package,
  BarChart3,
  Factory,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isRouteAllowed } from "@/lib/routeAccess";

// A compact set of 5 tabs — order = priority. Items the user's role
// can't see are auto-filtered.
const TABS = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/pos", label: "POS", icon: ShoppingCart },
  { to: "/petty-cash", label: "Cash", icon: Wallet },
  { to: "/inventory", label: "Stock", icon: Package },
  { to: "/productions", label: "Produce", icon: Factory },
  { to: "/invoicing", label: "Invoice", icon: Receipt },
  { to: "/analytics", label: "Insights", icon: BarChart3 },
];

/**
 * Fixed bottom navigation for mobile. Hidden on lg (>=1024). Shows the
 * top 5 role-allowed tabs so the app feels native on phones.
 */
export default function MobileBottomNav() {
  const { user, tenant } = useAuth();
  const role = user?.role;
  if (!role) return null;
  const grants = tenant?.active_role_module_grants || [];
  const allowed = TABS.filter((t) => isRouteAllowed(t.to, role, grants, tenant)).slice(0, 5);
  if (allowed.length === 0) return null;

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-card/95 backdrop-blur border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid" style={{ gridTemplateColumns: `repeat(${allowed.length}, minmax(0, 1fr))` }}>
        {allowed.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`mnav-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono uppercase tracking-widest transition-colors ${
                  isActive
                    ? "text-brand"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="leading-tight">{t.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
